import React, { type FC, type PropsWithChildren } from 'react';

import styled from '@emotion/styled';
import { variant } from '@lucid/theme';

import { type Lucid } from '../../../types/types';
import Card, { type CardElementProps } from '../../card/card';

export interface ResourceCardProps extends Omit<CardElementProps, 'divider'> {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Whether the text subcomponent should truncate */
  shouldTruncateText?: boolean;
  /** Whether the image height should be unified */
  shouldUnifyImageHeight?: boolean;
  /** The available variants for the Resource Card component. */
  variant?: Lucid.Theme.Component.Cards.ResourceCard.VariantKeys;
}

const StyledResourceCard = styled(Card)(
  variant({
    scale: 'cards.resourceCard',
  }),
);

const ResourceCard: FC<PropsWithChildren<ResourceCardProps>> = ({
  className,
  label,
  link,
  media,
  text,
  children,
  heading,
  tags,
  shouldUnifyImageHeight,
  shouldTruncateText,
  variant = 'default',
}) => {
  const shouldLinkWrapHeading = heading && link?.final_url && link.isFullCardLink && !link.default_link_text;

  return (
    <StyledResourceCard
      css={[
        {
          label: 'resourceCard',
        },
        shouldTruncateText && {
          '[data-card-element="text"], [data-card-element="heading"]': {
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            // required so that descenders don't get cut off
            paddingBottom: '0.1em',
          },
        },
      ]}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
      shadow="hover"
      shouldAnimateOnHover={variant !== 'gradient'}
      variant={variant}
    >
      {media && media.image?.filename_disk && (
        <Card.Media
          {...media}
          css={
            shouldUnifyImageHeight && [{ height: '220px', width: '220px', alignContent: 'center', alignSelf: 'center' }]
          }
        />
      )}
      {media && media.image?.filename_disk && variant !== 'gradient' && <Card.Divider type="fullBleed" />}
      {label && <Card.Label {...label} variant="labelXS400" />}
      {heading &&
        (shouldLinkWrapHeading ? (
          <Card.Link {...link}>
            <Card.Heading as="h3" heading={heading} />
          </Card.Link>
        ) : (
          <Card.Heading as="h3" heading={heading} />
        ))}
      {text && (
        <Card.Text {...text}>
          {/* If there is no accessible description, we'll try to render the link inside the Card.Text so that it's contextual */}
          {link?.final_url && !shouldLinkWrapHeading && !children && !link.accessibleDescription && (
            <Card.Link css={theme => ({ paddingTop: theme.space.s24 })} {...link} />
          )}
        </Card.Text>
      )}
      {children}
      {(children || !text || link?.accessibleDescription) && link?.final_url && !shouldLinkWrapHeading && (
        <Card.Link {...link} />
      )}
      {tags && <Card.Tags {...tags} />}
    </StyledResourceCard>
  );
};

export { ResourceCard };
export default ResourceCard;
